import { type CalendarDate, parseDate } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import type { inferRouterOutputs } from "@trpc/server";
import { Download } from "lucide-react";
import z from "zod";

import { isAdminRole } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { DatePicker } from "@/components/base/date-picker";
import { title } from "@/components/base/primitives";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@/components/base/table";
import { useTRPC } from "@/integrations/trpc/react";
import type { tournamentsRouter } from "@/integrations/trpc/routers/tournaments";
import { DefaultLayout } from "@/layouts/default";

const currentYear = new Date().getFullYear();
const defaultStartDate = `${currentYear}-01-01`;
const defaultEndDate = new Date().toISOString().slice(0, 10);

const searchSchema = z.object({
  startDate: z.string().optional().default(defaultStartDate),
  endDate: z.string().optional().default(defaultEndDate),
});

export const Route = createFileRoute("/admin/reports/tshirts")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [{ title: "CBVA - T-Shirt Report" }],
  }),
  loader: async ({ context }) => {
    const viewer = context.viewer;
    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

type TshirtRow = inferRouterOutputs<
  typeof tournamentsRouter
>["tshirtReport"][number];

function downloadCsv(rows: TshirtRow[]) {
  const header = "Director,Total,XS,SM,M,L,XL,XXL";
  const csvRows = rows.map((row) =>
    [
      `"${row.director.replace(/"/g, '""')}"`,
      row.total,
      row.xs,
      row.sm,
      row.m,
      row.l,
      row.xl,
      row.xxl,
    ].join(","),
  );

  const blob = new Blob([header + "\n" + csvRows.join("\n")], {
    type: "text/csv",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "tshirt-report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function RouteComponent() {
  const { startDate, endDate } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/reports/tshirts" });
  const trpc = useTRPC();

  const { data: rows, isLoading } = useQuery(
    trpc.tournaments.tshirtReport.queryOptions({ startDate, endDate }),
  );

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-8 w-full max-w-4xl px-3 py-12 mx-auto",
      }}
    >
      <section className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <h2 className={title({ size: "sm" })}>T-Shirt Report</h2>
          {rows && rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onPress={() => downloadCsv(rows)}
            >
              <Download className="mr-1 h-4 w-4" />
              Download CSV
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DatePicker<CalendarDate>
            label="Start Date"
            value={startDate ? parseDate(startDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  startDate: value?.toString() || undefined,
                }),
              });
            }}
          />
          <DatePicker<CalendarDate>
            label="End Date"
            value={endDate ? parseDate(endDate) : null}
            onChange={(value) => {
              navigate({
                replace: true,
                search: (search) => ({
                  ...search,
                  endDate: value?.toString() || undefined,
                }),
              });
            }}
          />
        </div>

        {isLoading ? (
          <p className="py-4 text-gray-500">Loading...</p>
        ) : !rows || rows.length === 0 ? (
          <p className="py-4 text-gray-500">No t-shirt pickups found.</p>
        ) : (
          <Table aria-label="T-shirt distribution by director">
            <TableHeader>
              <TableColumn isRowHeader>Director</TableColumn>
              <TableColumn>Total</TableColumn>
              <TableColumn>XS</TableColumn>
              <TableColumn>SM</TableColumn>
              <TableColumn>M</TableColumn>
              <TableColumn>L</TableColumn>
              <TableColumn>XL</TableColumn>
              <TableColumn>XXL</TableColumn>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.user_id}>
                  <TableCell>{row.director}</TableCell>
                  <TableCell>{row.total}</TableCell>
                  <TableCell>{row.xs}</TableCell>
                  <TableCell>{row.sm}</TableCell>
                  <TableCell>{row.m}</TableCell>
                  <TableCell>{row.l}</TableCell>
                  <TableCell>{row.xl}</TableCell>
                  <TableCell>{row.xxl}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold">
                <TableCell>Total</TableCell>
                <TableCell>
                  {rows.reduce((sum, r) => sum + r.total, 0)}
                </TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.xs, 0)}</TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.sm, 0)}</TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.m, 0)}</TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.l, 0)}</TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.xl, 0)}</TableCell>
                <TableCell>{rows.reduce((sum, r) => sum + r.xxl, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </section>
    </DefaultLayout>
  );
}
